import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';

import { ButtonModule, IconModule } from '@hglobal/common';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '@fb/environments';
import {
  BotChatMessageView,
  ChatMessageReplyTypes,
  ChatMessageStatus,
  InternalMessageStatus,
} from '@fb/models';
import { MessageDatePipe } from '@fb/pipes/date.pipe';
import { SlackLinkDirective } from '@fb/directives/slack-link.directive';
import { TranslateInPipe } from '@fb/pipes/translate-in.pipe';
import { ParseMarkdownPipe } from '@fb/pipes/parse-markdown.pipe';
import { SafeHtmlPipe } from '@fb/pipes/safe-html.pipe';

import { MessageReactionComponent } from '../message-reaction/message-reaction.component';
import { AiChatStore } from '../../store';
import { ChatMessageComponent } from '../chat-message/chat-message.component';

@Component({
  selector: 'bot-chat-message',
  templateUrl: './bot-chat-message.component.html',
  styleUrl: './bot-chat-message.component.scss',
  imports: [
    ChatMessageComponent,
    ButtonModule,
    MessageDatePipe,
    SlackLinkDirective,
    IconModule,
    TranslateModule,
    MessageReactionComponent,
    TranslateInPipe,
    ParseMarkdownPipe,
    SafeHtmlPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BotChatMessageComponent {
  #aiChatStore = inject(AiChatStore);

  message = input.required<BotChatMessageView>();
  isFirst = input<boolean>(false);
  chatStarted = input<string>(null);
  /** Whether this specific answer accepts feedback (false for client-only replies). */
  feedbackEligible = input<boolean>(false);

  text = computed(() => this.message().reply);
  links = computed(() => this.message().links || []);
  chatStartedAfter = computed(
    () => this.chatStarted() && this.message().createdAt < this.chatStarted(),
  );

  balances = computed(() => {
    const obj: Partial<Record<ChatMessageReplyTypes, number>> = {};

    this.message().timeOffBalances?.forEach(timeOffBalance => {
      obj[timeOffBalance.type] = timeOffBalance.value;
    });

    return obj;
  });

  displayReaction = computed(() => (
    this.feedbackEligible()
    && this.message().internalStatus === InternalMessageStatus.resolved
    && this.message().isSuccessReply
  ));

  internalMessageStatus = InternalMessageStatus;
  chatMessageStatus = ChatMessageStatus;
  chatMessageReplyTypes = ChatMessageReplyTypes;

  aiChatBotIcon = `${environment.shortName}-ai-chat-bot`;

  originLink = window.location.origin;

  tryAgain() {
    this.#aiChatStore.sendMessage(this.message().request);
  }

  randomRephrase(dateStr: string): number {
    const date = new Date(dateStr);
    const milliseconds = date.getMilliseconds();

    return milliseconds % 10;
  }
}
